import { FacebookAuthProvider, signInWithPopup } from 'firebase/auth';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { FacebookIcon, GoogleIcon } from '../../../assets/icons/icons';
import Header from '../../../components/Header/Header';
import Text from '../../../components/Text/Text';
import TextFieldd from '../../../components/TextField/TextField';
import { auth, providerFC, providerOAuth } from '../../../helper/firebase';
import useRootStore from '../../../hooks/useRootStore';
import { ButtonComponent } from '../../../utils/button';
import Colors from '../../../utils/colors';
import SocialBtn from '../../../utils/socialBtn';
import styles from './Login.module.css';
import { PiPassword } from 'react-icons/pi';
import { MdAlternateEmail } from 'react-icons/md';
import { useState } from 'react';

const Login = () => {
    const { t } = useTranslation();
    const navigation = useNavigate();
    const { loginEmailWithPassword, loginOAuth2, isLoginLoading } = useRootStore().authStore;
    const [visibility, setVisibility] = useState(false);

    const handleClickToggle = () => {
        setVisibility(prev => !prev);
    };

    const handleGoogleSignIn = async () => {
        // Non-null assertion: `auth` is null only when Firebase isn't
        // configured, in which case this throws here (click-time), not at
        // module load — see helper/firebase.ts.
        await signInWithPopup(auth!, providerOAuth)
            .then((result: any) => {
                loginOAuth2({
                    authType: 'googleToken',
                    oAuth2token: result._tokenResponse.oauthIdToken,
                    email: result.user.email,
                    userName: result.user.email,
                });
            })
            .catch(err => {
                console.log(err);
            });
    };

    const handleFacebookLogin = () => {
        signInWithPopup(auth!, providerFC)
            .then(result => {
                const credential = FacebookAuthProvider.credentialFromResult(result);
                console.log('facebook login success', result, credential);
            })
            .catch(err => {
                console.log(err);
            });
    };

    const validate = yup.object({
        email: yup
            .string()
            .trim()
            .required(`${t('error_message_email_req')}`)
            .email(`${t('error_message_email_form')}`),
        password: yup
            .string()
            .trim()
            .required(`${t('error_message_password_req')}`)
            // .matches(
            //     Regex.PasswordLogin,
            //     `${t("error_message_password_regex")}`
            // )
            .min(8, `${t('error_message_password_min')}`),
    });

    return (
        <div className={styles.headerBox}>
            <Header leftIcon="arrowLeft" text={`${t('Sign In')}`} onLeftIconPress={() => navigation('/auth/welcome')} />
            <div className={styles.login_container}>
                <div className={styles.formsContent}>
                    <Formik
                        initialValues={{
                            email: '',
                            password: '',
                        }}
                        validationSchema={validate}
                        onSubmit={values => {
                            loginEmailWithPassword(values);
                        }}
                    >
                        {formik => (
                            <div>
                                <Form>
                                    <TextFieldd
                                        name="email"
                                        type="email"
                                        placeholder="Your email"
                                        icon={<MdAlternateEmail color="#D9D9D9" />}
                                    />
                                    <TextFieldd
                                        name="password"
                                        type={visibility ? 'text' : 'password'}
                                        placeholder="Your password"
                                        showClick={() => handleClickToggle()}
                                        icon={<PiPassword color="#D9D9D9" />}
                                        visibility
                                    />
                                    <div className={styles.forgotBox}>
                                        <Text
                                            children={t('forgot_pass')}
                                            color={Colors.Black}
                                            fontFamily="Montserrat"
                                            fontWeight={400}
                                            fontSize="14px"
                                            handleLink={() => navigation('/auth/forgot-pass')}
                                        />
                                    </div>
                                    <ButtonComponent
                                        loadingType="bounce"
                                        isLoading={isLoginLoading}
                                        type={'submit'}
                                        width="100%"
                                        text="Login"
                                        height="45px"
                                    />
                                </Form>
                            </div>
                        )}
                    </Formik>
                </div>
                <Text
                    children={t('Or login with')}
                    color={Colors.Black}
                    fontFamily="Montserrat"
                    fontWeight={400}
                    fontSize="14px"
                    margin="100px 0 0px 0"
                />
                <div className={styles.socialContent}>
                    <SocialBtn icon={<FacebookIcon />} title={`${t('login_fb')}`} onClick={handleFacebookLogin} />
                    <SocialBtn
                        icon={<GoogleIcon />}
                        title={`${t('login_google')}`}
                        margin="20px 0 0 0"
                        onClick={handleGoogleSignIn}
                    />
                </div>
            </div>
        </div>
    );
};

export default observer(Login);
